import { Form } from 'antd';
import { FC, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { Moment } from 'moment/moment';
import { CountryCode } from 'libphonenumber-js/core';
import i18n from 'i18next';

import { saveContactInfo } from 'features/registration/actions';
import { getUserProfile } from 'features/user/actions';

import { useAppDispatch, useAppSelector } from 'hooks';
import { RegistrationLayout } from 'componets/layouts';

import { ClientRole, Paths, RequestStatus, ACCESS_TOKEN } from 'utils/constants';
import Utils from 'utils/utils';
import { getInStorage } from 'utils';
import { logout } from 'features/authorization/actions';
import { FieldData, ContactInfoCompanyProps, ContactInfoUserProps, Option } from 'models';
import { selectCountries, selectUser } from 'selectors';

import { SettingsDeleteAccount } from '../../../Account/Settings/SettingsDeleteAccount';
import { PersonalInformationCompany } from './PersonalInformationCompany';
import { PersonalInformationUser } from './PersonalInformationUser';

import styles from '../../index.module.scss';

const MIN_AGE = 18;

export const GENDER_OPTIONS = [
	{ value: 'Male', label: i18n.t('maleText') },
	{ value: 'Female', label: i18n.t('femaleText') },
];

export type PersonalInformationProps = {
	maxDate: Moment;
	onChangeSelect: (v: string) => void;
	countriesOptions: Option[];
	country: CountryCode;
	loading: boolean;
	isShowEconomicActivityOther?: boolean;
};

export const PersonalInformation: FC = () => {
	const navigate = useNavigate();
	const [form] = Form.useForm();

	const [fields, setFields] = useState<FieldData[]>([]);

	const status = useAppSelector((state) => state.registration.status);
	const user = useAppSelector(selectUser);
	const countries = useAppSelector(selectCountries);

	const isCompany = user.userType === ClientRole.COMPANY;

	const dispatch = useAppDispatch();

	useEffect(() => {
		if (!user?.status && Cookies.get(ACCESS_TOKEN)) {
			dispatch(getUserProfile());
		}
	}, []);

	const onFinish = (data: ContactInfoCompanyProps | ContactInfoUserProps) => {
		const countryId = countries?.find((item) => item.iso === data.countryId)?.id || '';
		const extra: Record<string, any> = {}
		if (data?.economicActivity) {
			extra.economicActivity = Number(data.economicActivity);
		}
		if (data?.citizenshipId) {
			extra.citizenshipId = Number(data.citizenshipId);
		}
		dispatch(
			saveContactInfo({
				...data,
				...extra,
				countryId,
				referralCode: data.referralCode
					? data.referralCode
					: typeof getInStorage('referralCode') === 'string'
					? JSON.parse(getInStorage('referralCode') || '')
					: '',
			}),
		)
			.unwrap()
			.then(() => {
				navigate(Paths.VERIFY2FA);
			})
			.catch(({ errors }) => {
				Utils.injectFormErrors(fields, errors, setFields);

				if (errors[0]?.code === 409) {
					dispatch(logout());
				}
				Utils.scrollToFirstError(errors, form);
			});
	};

	const countriesOptions = useMemo(
		() =>
			countries
				?.filter((item) => item.activeForRegistration)
				.map((item: { label: string; iso: string }) => {
					return {
						label: item.label,
						value: item.iso,
					};
				}) || [],
		[countries],
	);

	const maxDate = moment().subtract(MIN_AGE, 'year');

	const country = Form.useWatch('countryId', form);

	const props = {
		maxDate,
		loading: status === RequestStatus.PENDING,
		country,
		countriesOptions,
		onChangeSelect: (v: string) => {
			const country = countries?.find((item: { id: any }) => item.id === v)?.iso;

			form.setFieldValue('countryId', country);
		},
		isShowEconomicActivityOther: form.getFieldValue('economicActivity') == 25,
	};

	return (
		<RegistrationLayout email={user.email}>
			<Form
				scrollToFirstError={{
					behavior: 'smooth',
					block: 'center',
				}}
				onFinish={onFinish}
				onFieldsChange={setFields}
				fields={fields}
				form={form}
				initialValues={{
					referralCode:
						typeof getInStorage('referralCode') === 'string'
							? JSON.parse(getInStorage('referralCode') || '')
							: '',
				}}
			>
				{isCompany ? (
					<PersonalInformationCompany {...props} />
				) : (
					<PersonalInformationUser {...props} />
				)}
			</Form>
			<div className={styles.delete}>
				<SettingsDeleteAccount />
			</div>
		</RegistrationLayout>
	);
};
